import React, { useState, useEffect } from 'react';
import Footer from '../../components/HeaderFooter/Footer';
import {
  Mail, Phone, MapPin, Send, Clock, Globe, CheckCircle, AlertCircle, Linkedin, Twitter, Facebook
} from 'lucide-react';
import StandardSpinner from '../../utils/Spinner';
import { useDispatch } from 'react-redux';
import { Post_Contact } from '../../services/oprations/Contact';
import { useTheme } from '../../Context/themeContext';

const Contact = () => {
  const dispatch = useDispatch();
  const { theme } = useTheme();
  const [loading, setloading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [formStatus, setFormStatus] = useState({
    submitted: false,
    success: false,
    message: ''
  });

  const [activeField, setActiveField] = useState(null);

  // Theme-aware classes
  const isDark = theme === 'dark';
  
  const bgPrimary = isDark ? 'bg-richblue-950' : 'bg-gray-50';
  const bgCard = isDark ? 'bg-richblue-800/30' : 'bg-white/80';
  const bgCardHover = isDark ? 'hover:bg-richblue-800/80' : 'hover:bg-gray-100/80';
  const bgInput = isDark ? 'bg-richblue-900/50' : 'bg-white/90';
  const bgFaq = isDark ? 'bg-richblue-900/50' : 'bg-gray-100/80';
  const bgFaqHover = isDark ? 'hover:bg-richblue-800/80' : 'hover:bg-gray-200/80';
  const bgSocial = isDark ? 'bg-richblue-900/50' : 'bg-gray-100/80';
  const bgSocialHover = isDark ? 'hover:bg-blue-600/20' : 'hover:bg-blue-50';
  
  const borderColor = isDark ? 'border-blue-800/40' : 'border-gray-200';
  const borderHover = isDark ? 'hover:border-blue-700/60' : 'hover:border-blue-400';
  const borderFaq = isDark ? 'border-blue-800/30' : 'border-gray-200';
  const borderFaqHover = isDark ? 'hover:border-blue-700/50' : 'hover:border-blue-400';
  const borderSocial = isDark ? 'border-blue-800/40' : 'border-gray-200';
  const borderSocialHover = isDark ? 'hover:border-blue-600/60' : 'hover:border-blue-400';
  
  const textPrimary = isDark ? 'text-white' : 'text-gray-900';
  //const textSecondary = isDark ? 'text-blue-200/70' : 'text-gray-600';
  //const textLight = isDark ? 'text-blue-200' : 'text-gray-700';
  //const textMuted = isDark ? 'text-blue-200/70' : 'text-gray-600';
  const textPlaceholder = isDark ? 'placeholder-blue-400/30' : 'placeholder-gray-400';
  //const textFaqTitle = isDark ? 'text-white hover:text-blue-300' : 'text-gray-900 hover:text-blue-600';
  //const textFaqAnswer = isDark ? 'text-blue-200/70' : 'text-gray-600';
  
  const gradientText = 'bg-gradient-to-r from-blue-400 to-purple-400 text-transparent bg-clip-text';
  const gradientButton = isDark 
    ? 'bg-gradient-to-r from-blue-800 to-blue-900 hover:from-blue-700 hover:to-blue-700'
    : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600';
  const shadowColor = isDark ? 'shadow-blue-900/20' : 'shadow-blue-200/50';
  
  const successBg = isDark ? 'bg-green-900/50 border-green-700/50 text-green-300' : 'bg-green-50 border-green-200 text-green-700';
  const errorBg = isDark ? 'bg-red-900/50 border-red-700/50 text-red-300' : 'bg-red-50 border-red-200 text-red-700';
  
  const iconBg = isDark ? 'bg-gradient-to-br from-blue-600 to-blue-800' : 'bg-gradient-to-br from-blue-500 to-blue-700';

  useEffect(() => {
    setTimeout(() => {
      setloading(false);
    }, 800);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const res = await dispatch(Post_Contact(formData));
    if (res?.success) {
      setFormStatus({
        submitted: true,
        success: true,
        message: 'Thank you for reaching out! We\'ll get back to you within 24 hours.'
      });
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
      setTimeout(() => {
        setFormStatus({
          submitted: false,
          success: false,
          message: ''
        });
      }, 5000);
    } else {
      setFormStatus({
        submitted: true,
        success: false,
        message: res?.message || 'Something went wrong. Please try again.'
      });
      setTimeout(() => {
        setFormStatus({
          submitted: false,
          success: false,
          message: ''
        });
      }, 5000);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email Us',
      details: ['sales@bandy-moot.com'],
      action: 'Send a message',
      color: 'blue'
    },
    {
      icon: Phone,
      title: 'Call Us',
      details: ['+91 7905226299'],
      action: 'Request a call',
      color: 'blue'
    },
    {
      icon: MapPin,
      title: 'Visit Us',
      details: ['Vasu Bhawan Near Prayag Dhaba Andawa', 'Prayagraj Uttar Pradesh, India 211019'],
      action: 'Get directions',
      color: 'blue'
    },
    {
      icon: Clock,
      title: 'Business Hours',
      details: ['Mon-Fri: 9:00 AM - 6:00 PM', 'Sat-Sun: Closed'],
      action: 'Schedule a meeting',
      color: 'blue'
    }
  ];

  const faqs = [
    {
      question: 'How quickly do you respond?',
      answer: 'We aim to respond to all inquiries within 24 hours during business days.'
    },
    {
      question: 'Do you provide technical support?',
      answer: 'Yes, we offer 24/7 technical support for enterprise customers and business hours support for all users.'
    },
    {
      question: 'Can I schedule a demo?',
      answer: 'Absolutely! You can schedule a personalized demo through our contact form or by calling our sales team.'
    }
  ];

  const socialLinks = [
    { icon: Linkedin, href: 'https://www.linkedin.com/company/bandy-moot-private-limited', label: 'LinkedIn' },
    { icon: Twitter, href: 'https://x.com/BandyMoot', label: 'Twitter' },
    { icon: Facebook, href: 'https://www.facebook.com/profile.php?id=61579466615993', label: 'Facebook' }
  ];

  if (loading) {
    return <StandardSpinner />;
  }

  return (
    <div className={`min-h-screen ${bgPrimary} transition-colors duration-300`}>
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="relative px-4 py-24 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className={`text-5xl md:text-6xl font-bold ${textPrimary} mb-6`}>
              Get in{' '}
              <span className={gradientText}>
                Touch
              </span>
            </h1>
            <p className={`text-xl ${isDark ? 'text-blue-200/80' : 'text-gray-600'} max-w-2xl mx-auto`}>
              Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
            </p>
          </div>
        </div>
      </div>

      {/* Contact Info Cards */}
      <div className="px-4 mx-auto -mt-12 max-w-7xl sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {contactInfo.map((item, index) => {
            const Icon = item.icon;
            const colorClasses = {
              blue: 'from-blue-600 to-blue-800',
              purple: 'from-purple-600 to-purple-800',
              indigo: 'from-indigo-600 to-indigo-800',
              cyan: 'from-cyan-600 to-cyan-800'
            };

            return (
              <div
                key={index}
                className={`group relative ${bgCard} backdrop-blur-sm border ${borderColor} rounded-2xl p-6 ${bgCardHover} transition-all duration-300 hover:scale-105 hover:shadow-2xl ${shadowColor}`}
              >
                <div className={`absolute inset-0 bg-gradient-to-r ${colorClasses[item.color]} opacity-0 group-hover:opacity-10 rounded-2xl transition-opacity duration-300`}></div>

                <div className={`w-14 h-14 rounded-xl ${iconBg} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  <Icon className="text-white w-7 h-7" />
                </div>

                <h3 className={`text-xl font-semibold ${textPrimary} mb-2`}>{item.title}</h3>

                {item.details.map((detail, idx) => (
                  <p key={idx} className={`text-sm mb-1 ${isDark ? 'text-blue-200/70' : 'text-gray-600'}`}>{detail}</p>
                ))}
              </div>
            );
          })}
        </div>
      </div>

      {/* Main Contact Section */}
      <div className="px-4 py-20 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-2">
          {/* Contact Form */}
          <div className={`${bgCard} backdrop-blur-sm border ${borderColor} rounded-3xl p-8 transition-colors duration-300`}>
            <h2 className={`text-3xl font-bold ${textPrimary} mb-2`}>Send us a message</h2>
            <p className={`${isDark ? 'text-blue-200/70' : 'text-gray-600'} mb-8`}>Fill out the form below and we'll get back to you shortly.</p>

            {formStatus.submitted && (
              <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${formStatus.success ? successBg : errorBg}`}>
                {formStatus.success ? (
                  <CheckCircle className="flex-shrink-0 w-5 h-5" />
                ) : (
                  <AlertCircle className="flex-shrink-0 w-5 h-5" />
                )}
                <p className="text-sm">{formStatus.message}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name Field */}
              <div className="relative">
                <label className={`block text-sm font-medium ${isDark ? 'text-blue-200' : 'text-gray-700'} mb-2`}>
                  Full Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  onFocus={() => setActiveField('name')}
                  onBlur={() => setActiveField(null)}
                  required
                  className={`w-full ${bgInput} border rounded-xl px-4 py-3.5 ${textPrimary} ${textPlaceholder} focus:outline-none transition-all duration-300 ${
                    activeField === 'name'
                      ? 'border-blue-500 ring-2 ring-blue-500/20'
                      : `${borderColor} ${borderHover}`
                  }`}
                  placeholder="John Doe"
                />
              </div>

              {/* Email Field */}
              <div className="relative">
                <label className={`block text-sm font-medium ${isDark ? 'text-blue-200' : 'text-gray-700'} mb-2`}>
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onFocus={() => setActiveField('email')}
                  onBlur={() => setActiveField(null)}
                  required
                  className={`w-full ${bgInput} border rounded-xl px-4 py-3.5 ${textPrimary} ${textPlaceholder} focus:outline-none transition-all duration-300 ${
                    activeField === 'email'
                      ? 'border-blue-500 ring-2 ring-blue-500/20'
                      : `${borderColor} ${borderHover}`
                  }`}
                  placeholder="john@example.com"
                />
              </div>

              {/* Subject Field */}
              <div className="relative">
                <label className={`block text-sm font-medium ${isDark ? 'text-blue-200' : 'text-gray-700'} mb-2`}>
                  Subject
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  onFocus={() => setActiveField('subject')}
                  onBlur={() => setActiveField(null)}
                  required
                  className={`w-full ${bgInput} border rounded-xl px-4 py-3.5 ${textPrimary} ${textPlaceholder} focus:outline-none transition-all duration-300 ${
                    activeField === 'subject'
                      ? 'border-blue-500 ring-2 ring-blue-500/20'
                      : `${borderColor} ${borderHover}`
                  }`}
                  placeholder="How can we help?"
                />
              </div>

              {/* Message Field */}
              <div className="relative">
                <label className={`block text-sm font-medium ${isDark ? 'text-blue-200' : 'text-gray-700'} mb-2`}>
                  Message
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  onFocus={() => setActiveField('message')}
                  onBlur={() => setActiveField(null)}
                  required
                  rows="5"
                  className={`w-full ${bgInput} border rounded-xl px-4 py-3.5 ${textPrimary} ${textPlaceholder} focus:outline-none transition-all duration-300 resize-none ${
                    activeField === 'message'
                      ? 'border-blue-500 ring-2 ring-blue-500/20'
                      : `${borderColor} ${borderHover}`
                  }`}
                  placeholder="Tell us about your inquiry..."
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className={`w-full ${gradientButton} text-white font-semibold py-4 px-6 rounded-xl transition-all duration-300 transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-blue-500/50 flex items-center justify-center gap-2 group`}
              >
                <Send className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                Send Message
              </button>
            </form>
          </div>

          {/* Right Side - FAQ & Social */}
          <div className="space-y-8">
            {/* FAQ Section */}
            <div className={`${bgCard} backdrop-blur-sm border ${borderColor} rounded-3xl p-8 transition-colors duration-300`}>
              <h2 className={`text-3xl font-bold ${textPrimary} mb-6`}>Frequently Asked Questions</h2>

              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className={`group ${bgFaq} rounded-xl p-5 ${bgFaqHover} transition-all duration-300 cursor-pointer border ${borderFaq} ${borderFaqHover}`}
                  >
                    <h3 className={`text-lg font-semibold ${textPrimary} mb-2 group-hover:text-blue-600 transition-colors`}>
                      {faq.question}
                    </h3>
                    <p className={`${isDark ? 'text-blue-200/70' : 'text-gray-600'} text-sm leading-relaxed`}>
                      {faq.answer}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Social Links */}
            <div className={`${bgCard} backdrop-blur-sm border ${borderColor} rounded-3xl p-8 transition-colors duration-300`}>
              <h3 className={`text-xl font-semibold ${textPrimary} mb-4`}>Connect With Us</h3>
              <div className="flex items-center gap-4">
                {socialLinks.map((social, index) => {
                  const Icon = social.icon;
                  return (
                    <a
                      key={index}
                      href={social.href}
                      aria-label={social.label}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`w-12 h-12 rounded-xl ${bgSocial} border ${borderSocial} flex items-center justify-center ${bgSocialHover} ${borderSocialHover} transition-all duration-300 group`}
                    >
                      <Icon className={`w-5 h-5 ${isDark ? 'text-blue-400 group-hover:text-blue-300' : 'text-blue-600 group-hover:text-blue-500'} transition-colors`} />
                    </a>
                  );
                })}
              </div>

              <div className={`mt-6 pt-6 border-t ${isDark ? 'border-blue-800/40' : 'border-gray-200'}`}>
                <div className={`flex items-center gap-3 ${isDark ? 'text-blue-200/70' : 'text-gray-600'}`}>
                  <Globe className="w-5 h-5" />
                  <span className="text-sm">www.bandy-moot.com</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* CTA Section */}
      <Footer />
    </div>
  );
};

export default Contact;
